// src/services/sharePointLibraryApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

// SharePoint Library Types
export interface SharePointLibrary {
  libraryId: number;
  libraryName: string;
  displayName: string;
  siteId: string;
  driveId: string | null;
  webUrl: string | null;
  isActive: boolean;
  isDefault: boolean;
  createdAt?: string;
  lastUpdated?: string;
  createdBy?: string | null;
  lastUpdatedBy?: string | null;
}

// Global SharePoint Settings (default destination wrapper)
export interface GlobalSharePointSettings {
  libraryId?: number;
  libraryName: string;
  displayName?: string;
  siteId: string;
  webUrl: string | null;
  isDefault?: boolean;
}

export interface UpdateGlobalSharePointSettingsRequest {
  libraryName: string;
  siteId: string;
  webUrl?: string | null;
  displayName?: string | null;
}

export interface CreateLibraryRequest {
  libraryName: string;
  siteId: string;
  webUrl?: string;
  displayName?: string;
  isDefault?: boolean;
}

export interface UpdateLibraryRequest {
  libraryName?: string;
  siteId?: string;
  webUrl?: string;
  displayName?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

export interface LibrariesListResponse {
  libraries: SharePointLibrary[];
}

export interface SharePointLibraryApiError {
  error: boolean;
  message: string;
  code?: string;
  errors?: unknown;
}

async function parseError(response: Response, fallback: string): Promise<SharePointLibraryApiError> {
  try {
    const error = await response.json();
    return {
      error: true,
      message: error.message || fallback,
      code: error.code || 'NETWORK_ERROR',
      errors: error.errors,
    };
  } catch {
    return { error: true, message: fallback, code: 'NETWORK_ERROR' };
  }
}

/**
 * Get all SharePoint libraries (admin)
 */
export async function getAllSharePointLibraries(includeInactive = true): Promise<LibrariesListResponse> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries?includeInactive=${includeInactive ? 'true' : 'false'}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to load libraries: ${response.statusText}`);
  }

  const data = await response.json();
  if (Array.isArray(data)) {
    return { libraries: data };
  }
  return data;
}

/**
 * Active destinations for planning / folder pickers (authenticated project users)
 */
export async function getActiveSharePointDestinations(projectId: number): Promise<SharePointLibrary[]> {
  const url = `${API_BASE_URL}/api/projects/${projectId}/sharepoint-libraries`;
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to load SharePoint destinations');
  }

  const data = await response.json();
  const list = data.libraries || data || [];
  return (list as Array<Record<string, unknown>>).map((lib) => ({
    libraryId: Number(lib.libraryId),
    libraryName: String(lib.name || lib.libraryName || ''),
    displayName: String(lib.displayName || lib.name || lib.libraryName || ''),
    siteId: String(lib.siteId || ''),
    driveId: (lib.driveId as string | null) || null,
    webUrl: (lib.webUrl as string | null) || null,
    isActive: true,
    isDefault: !!lib.isDefault,
  }));
}

export async function getSharePointLibrary(libraryId: number): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to load library: ${response.statusText}`);
  }

  return response.json();
}

export async function createSharePointLibrary(data: CreateLibraryRequest): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      libraryName: data.libraryName.trim(),
      siteId: data.siteId.trim(),
      webUrl: data.webUrl?.trim() || undefined,
      displayName: data.displayName?.trim() || undefined,
      isDefault: data.isDefault === true,
    }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to create library: ${response.statusText}`);
  }

  return response.json();
}

export async function updateSharePointLibrary(
  libraryId: number,
  data: UpdateLibraryRequest
): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;

  const cleanedData: UpdateLibraryRequest = {};
  if (data.libraryName !== undefined && data.libraryName !== null && data.libraryName.trim() !== '') {
    cleanedData.libraryName = data.libraryName.trim();
  }
  if (data.displayName !== undefined) {
    cleanedData.displayName = data.displayName === null ? undefined : String(data.displayName).trim();
  }
  if (data.siteId !== undefined && data.siteId !== null && data.siteId.trim() !== '') {
    cleanedData.siteId = data.siteId.trim();
  }
  if (data.webUrl !== undefined) {
    cleanedData.webUrl =
      data.webUrl === null || (typeof data.webUrl === 'string' && data.webUrl.trim() === '')
        ? undefined
        : typeof data.webUrl === 'string'
          ? data.webUrl.trim()
          : undefined;
  }
  if (data.isActive !== undefined) {
    cleanedData.isActive = data.isActive;
  }
  if (data.isDefault !== undefined) {
    cleanedData.isDefault = data.isDefault;
  }

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(cleanedData),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to update library: ${response.statusText}`);
  }

  return response.json();
}

export async function setDefaultSharePointLibrary(libraryId: number): Promise<SharePointLibrary> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}/set-default`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (!response.ok) {
    throw await parseError(response, 'Failed to set default SharePoint destination');
  }

  return response.json();
}

export async function deleteSharePointLibrary(libraryId: number): Promise<void> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-libraries/${libraryId}`;

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Library not found', code: 'NOT_FOUND' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to delete library: ${response.statusText}`);
  }
}

export async function getGlobalSharePointSettings(): Promise<GlobalSharePointSettings> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-settings`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to load SharePoint settings: ${response.statusText}`);
  }

  return response.json();
}

export async function updateGlobalSharePointSettings(
  data: UpdateGlobalSharePointSettingsRequest
): Promise<GlobalSharePointSettings> {
  const url = `${API_BASE_URL}/api/admin/sharepoint-settings`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({
      libraryName: data.libraryName.trim(),
      siteId: data.siteId.trim(),
      webUrl: data.webUrl?.trim() || null,
      displayName: data.displayName?.trim() || undefined,
    }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'System Admin access required', code: 'AUTH_REQUIRED' } as SharePointLibraryApiError;
  }

  if (!response.ok) {
    throw await parseError(response, `Failed to update SharePoint settings: ${response.statusText}`);
  }

  return response.json();
}
